/**
 * Browser-side @skypointcloud/logger bootstrap (GUIDELINES.md §4).
 *
 * Importing this module initializes the logger exactly once, which is why it is
 * the FIRST import in main.tsx — before any module that might log while it is
 * being evaluated. Re-exports `logger`, `reactPlugin` and `withAITracking` so
 * callers can:
 *
 *   import { logger } from "@/lib/logger";
 *   logger.info("connection saved", { "skydata.surface": surface });
 *
 * `console.*` is an ESLint error; this is the only logging path in the client.
 *
 * The App Insights connection string is read at runtime from the document the web serves
 * (`shared/browserTelemetry.ts`, #851), never compiled into the bundle, so one image reports to
 * whichever environment runs it. No tag (a local run, or an environment without one) means the
 * console only: no SDK is loaded and nothing fails.
 */
import { initLogger, logger, withAITracking } from "@skypointcloud/logger";
import { readBrowserTelemetry } from "@shared/browserTelemetry";

/** The slice of the App Insights client the guard below touches. */
interface UnloadableClient {
  unload: (isAsync?: boolean) => unknown;
  flush?: (isAsync?: boolean) => unknown;
}

/**
 * S3: the logger package unloads the App Insights SDK on every `visibilitychange` to hidden.
 * An unloaded SDK throws "SDK is not initialized" when unloaded again, so the second time the
 * tab was hidden the page threw an uncaught error, and nothing was reported after the first hide.
 * Hiding the tab now sends what is queued and leaves the SDK running. The package keeps its
 * client private, so it is read by name and the guard does nothing when it is not there.
 */
export function flushInsteadOfUnload(transport: unknown): void {
  const client = (transport as { client?: UnloadableClient } | undefined)?.client;
  if (!client || typeof client.unload !== "function") return;
  client.unload = (isAsync?: boolean) => {
    try {
      return client.flush?.(isAsync !== false);
    } catch {
      return undefined; // telemetry never breaks the page
    }
  };
}

const { reactPlugin, appInsights, ready } = initLogger({
  appName: "skydata-web",
  environment: import.meta.env.MODE,
  appInsightsConnectionString: readBrowserTelemetry(
    typeof document === "undefined" ? undefined : document,
  ),
});

// The SDK starts in the background; the guard goes on as soon as it is up.
void Promise.resolve(ready).then(() => flushInsteadOfUnload(appInsights));

export { logger, reactPlugin, withAITracking };
